import { Clickable } from '@carvana/merch-components';
import classnames from 'classnames';
import { type PropsWithChildren, useContext, useMemo, useState } from 'react';
import Slider from 'react-slick';
import { VehicleTile } from '@/giveaway/VehicleTile';
import Sections from '~/constants/giveaway/sections';
import DeviceContext from '~/context/common/DeviceContext';
import { useGetVehicles } from '~/hooks/giveaway/services';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import LoadingVehicles from './LoadingVehicles';

const VehicleFlexBox = ({ children }: PropsWithChildren) => (
  <div className="m-auto flex max-w-[1100px] flex-wrap justify-center gap-16 px-24">
    {children}
  </div>
);

const Dot = ({ isActive }: { isActive: boolean }) => (
  <Clickable
    className={`${
      isActive ? 'bg-blue-3' : 'bg-grey-2'
    } h-[8px] w-[8px] rounded-full transition-all duration-500`}
    data-analytics-id="Carousel Dot"
  />
);

function VehicleSlider({ children }: { children: React.ReactNode }) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const settings = useMemo(
    () => ({
      arrows: false,
      beforeChange: (_, newSlide) => setCurrentSlide(newSlide),
      centerMode: true,
      centerPadding: '10px',
      dots: true,
      customPaging: i => <Dot isActive={currentSlide === i} />,
      dotsClass: 'bottom-0 flex gap-4 justify-center pt-24 w-full',
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1
    }),
    [currentSlide]
  );

  return <Slider {...settings}>{children}</Slider>;
}

const filtersAWD = {
  driveTypes: ['Awd'],
  bodyStyles: ['Suv', 'Pickup'],
  price: { max: 40000 }
};

const filters4WD = {
  driveTypes: ['4wd'],
  bodyStyles: ['Suv', 'Pickup'],
  price: { max: 40000 }
};

function VehiclesContainer({ children }: { children?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-32 py-56 m:gap-48 m:py-80 l:gap-56">
      <div className="m-auto flex max-w-[680px] flex-col gap-16 px-24 text-center m:gap-24 m:px-40 l:px-0">
        <div className="text-[24px] font-black uppercase leading-none tracking-tight text-blue-6 m:text-[40px] l:text-[54px]">
          Ready for
          <br />
          four wheels?
        </div>
        <div className="text-grey-5 tl-body-m m:tl-body-l">
          Jaxson digs a 4WD vehicle with lots of cargo storage, and some kick to
          it. Check out some of our available vehicles below.
        </div>
      </div>
      {children}
      <div className="w-full px-24">
        <a
          className="border-r4 m-auto flex h-[56px] w-full items-center justify-center rounded bg-blue-2 p-20 text-white tl-label-m hover:bg-blue-3 active:bg-blue-4 m:max-w-[250px]"
          data-analytics-id="See More Vehicles"
          href="/cars"
        >
          Shop All Vehicles
        </a>
      </div>
    </div>
  );
}

function Vehicles() {
  const {
    deviceType: { isMobile }
  } = useContext(DeviceContext);
  const VehicleTileContainer = isMobile ? VehicleSlider : VehicleFlexBox;
  const [isAWDSelected, setIsAWDSelected] = useState(false);
  const vehicles4WD = useGetVehicles(filters4WD, true);
  const vehiclesAWD = useGetVehicles(filtersAWD, isAWDSelected);
  const { error, vehicles } = isAWDSelected ? vehiclesAWD : vehicles4WD;

  return (
    <VehiclesContainer>
      <div className="relative m-auto flex h-[40px] w-[330px] items-center rounded-full border border-grey-2 bg-grey-1">
        <div
          className={classnames(
            'absolute left-[-1px] top-[-1px] z-0 h-[40px] w-[175px] transform rounded-full border border-blue-4 bg-white transition-all duration-500',
            { 'translate-x-[155px]': isAWDSelected }
          )}
        ></div>
        <Clickable
          className={classnames(
            'z-1 flex h-full flex-1 items-center justify-center text-center text-blue-6 transition-colors duration-500 tl-label-m',
            {
              'text-grey-5': isAWDSelected
            }
          )}
          data-analytics-id="Toggled 4WD"
          onClick={() => setIsAWDSelected(false)}
        >
          4WD
        </Clickable>
        <Clickable
          className={classnames(
            'z-1 flex h-full flex-1 items-center justify-center text-center text-blue-6 transition-colors duration-500 tl-label-m',
            {
              'text-grey-5': !isAWDSelected
            }
          )}
          data-analytics-id="Toggled AWD"
          onClick={() => setIsAWDSelected(true)}
        >
          AWD
        </Clickable>
      </div>
      <VehicleTileContainer>
        {vehicles.length === 0 ? (
          <LoadingVehicles error={error} />
        ) : (
          vehicles.map(vehicle => (
            <VehicleTile key={vehicle.vehicleId} vehicle={vehicle} />
          ))
        )}
      </VehicleTileContainer>
    </VehiclesContainer>
  );
}

export default withErrorBoundary(
  Vehicles,
  () => <VehiclesContainer />,
  undefined,
  Sections.Vehicles
);
